import { RoutingLink } from "#batteries-included-components/RoutingLink";
import {
  useApplyConfiguration,
  useGetOneEstimationMethod,
  useGetOneEstimationMethodConfiguration,
  useGetOneEstimationMethodRun,
  useRunEstimationMethod,
  useUpdateEstimationMethodConfiguration,
} from "#hooks/adapters/useEstimationMethods";
import { useListFlows } from "#hooks/adapters/useFlows";
import {
  useClearNetworksCache,
  useGetOneNetwork,
} from "#hooks/adapters/useNetworks";
import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import { FlowRecordDetailRoute } from "#routes/organization/flows/[flowId]/detail/record/[recordId]";
import { NetworkDetailPageParameters } from "#routes/organization/networks/[networkId]/detail";
import { useNetworkCalculationFlowHeaders } from "#routes/organization/networks/[networkId]/detail/NetworkCalculationTab/NetworkCalculationTab.helpers";
import type { CalculationsViewFilterAreaType } from "#src/batteries-included-components/FilterAreas/CalculationsFilterAreas";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { getMeasurementTypeFromNetworkCalculatorEstimationMethod } from "#src/view-models/asset-groups";
import {
  Button,
  colours,
  DataDisplayWithAdornment,
  DataTable,
  DataTablePanel,
  HeaderType,
  Icon,
  StorageKeys,
  Switch,
} from "@validereinc/common-components";
import { AssetType, FlowType, SortDirection } from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toTitleCaseString,
  yearMonthName,
} from "@validereinc/utilities";
import React from "react";

export const NetworkCalculationConsumersPanel = ({
  viewConfigStorageKey,
  tableConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "tableConfigStorageKey" | "tableStateStorageKey"
>) => {
  const { networkId } = useParams<NetworkDetailPageParameters>();
  const {
    data: assetGroup,
    isLoading: isNetworkLoading,
    isFetching: isNetworkFetching,
  } = useGetOneNetwork(networkId);
  const { getUnitName } = useMeasurementTypes();
  const [viewFilters] = useStickyState<Partial<CalculationsViewFilterAreaType>>(
    {},
    viewConfigStorageKey
  );

  const filters = {
    id:
      // @ts-expect-error TODO: Typing on assetGroup
      assetGroup?.assets
        ?.filter(
          ({ asset_type, id }: any) =>
            asset_type === AssetType.FLOW &&
            // @ts-expect-error TODO: Typing on assetGroup
            !assetGroup?.network_inlet_ids?.includes(id)
        )
        .map(({ id }) => id) ?? [],
  };

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const {
    data: flows,
    isLoading: isFlowsLoading,
    isFetching: isFlowsFetching,
  } = useListFlows(
    {
      page: tableState.page,
      pageSize: tableState.pageSize,
      sortBy: tableState.sortBy,
      sortDirection: tableState.sortDirection,
      filters,
    },
    { enabled: !!filters.id.length }
  );

  const yearMonth = viewFilters?.date_range
    ? getYearMonthFromDateRange(viewFilters?.date_range)
    : "";
  const configId: any = {
    id: assetGroup?.default_estimation_method_id,
    assetType: AssetType.ASSET_GROUP,
    yearMonth,
  };

  const applyConfiguration = useApplyConfiguration();
  const onClickApplyConfiguration = () => {
    applyConfiguration.mutate(configId);
  };

  const runEstimationMethod = useRunEstimationMethod();
  const onClickSave = () => {
    runEstimationMethod.mutate({ ...configId, promoteToRecord: true });
  };

  const {
    data: configuration,
    isLoading: isConfigurationLoading,
    isFetching: isConfigurationFetching,
  } = useGetOneEstimationMethodConfiguration(configId);
  const {
    data: run,
    isLoading: isRunLoading,
    isFetching: isRunFetching,
  } = useGetOneEstimationMethodRun(configId);
  const {
    data: defaultEstimationMethod,
    isLoading: isEstimationMethodLoading,
  } = useGetOneEstimationMethod({
    id: assetGroup?.default_estimation_method_id ?? "",
    entityType: AssetType.ASSET_GROUP,
  });
  const measurementType = defaultEstimationMethod
    ? getMeasurementTypeFromNetworkCalculatorEstimationMethod(
        defaultEstimationMethod.data
      )
    : "";
  const measurementTypeHeader = toTitleCaseString(measurementType);
  const {
    isLoading: isLoadingNetworkCalculationFlowHeaders,
    headers: networkCalculationFlowHeaders,
  } = useNetworkCalculationFlowHeaders();
  const { invalidate } = useClearNetworksCache();

  const updateEstimationMethodConfiguration =
    useUpdateEstimationMethodConfiguration({
      onSuccess: () => {
        invalidate();
      },
    });

  const handleAdjustValueToggle = async (
    flowId: string,
    isChecked: boolean
  ) => {
    const calculatorVersion = configuration?.calculator_version;
    const previousProrationValue =
      configuration?.asset_inputs?.[flowId]?.is_prorated;

    return updateEstimationMethodConfiguration.mutateAsync({
      ...configId,
      data: {
        calculator_version: calculatorVersion,
        asset_inputs: {
          [flowId]: {
            is_prorated: {
              // If we don't spread the old value, we lose the unit/type/quantity config
              ...previousProrationValue,
              value: isChecked,
            },
          },
        },
      },
    });
  };

  const headers: Array<HeaderType<FlowType>> = [
    ...networkCalculationFlowHeaders,
    {
      key: "adjustable",
      label: "Adjust Value",
      renderComponent: ({ item }) => {
        return (
          <>
            <DataDisplayWithAdornment
              showAdornment={!configuration}
              adornmentSlot={() => (
                <Icon
                  variant="warning"
                  colour={colours.colours.status.warning}
                />
              )}
              detailsSlotDisplayMode="tooltip"
              detailsSlot={
                <>
                  A configuration for this period does not exist. Apply a
                  configuration for this period first.
                </>
              }
            >
              {/* @ts-expect-error missing props */}
              <Switch
                id={item.id}
                isDisabled={
                  !configuration ||
                  updateEstimationMethodConfiguration.isLoading ||
                  isLoading
                }
                value={
                  !!(configuration as any)?.asset_inputs?.[item.id]?.is_prorated
                    ?.value
                }
                onChange={(isChecked) => {
                  handleAdjustValueToggle(item.id, isChecked);
                }}
              />
            </DataDisplayWithAdornment>
          </>
        );
      },
    },
    {
      key: "volume_record_id",
      label: "Source",
      renderComponent: ({ item }: { item: FlowType }) => {
        const input = (configuration as any)?.asset_input_sources?.[item.id]?.[
          measurementType
        ];

        if (!input) {
          return "-";
        }

        return (
          <RoutingLink
            to={FlowRecordDetailRoute.toLink({
              pathParams: { flowId: item.id, recordId: input.record_id },
            })}
          >
            {`${item.name} - ${yearMonthName(yearMonth ?? "")}`}
          </RoutingLink>
        );
      },
    },
    {
      key: "volume_source_type",
      label: "Source Type",
      renderComponent: ({ item }: { item: FlowType }) => {
        const input = (configuration as any)?.asset_input_sources?.[item.id]?.[
          measurementType
        ];
        return input ? "Record" : "-";
      },
    },
    {
      key: "volume",
      label: `${measurementTypeHeader} Input`,
      alignment: "right",
      renderComponent: ({ item }) => {
        const input = (configuration as any)?.asset_inputs?.[item.id]?.[
          measurementType
        ];

        return (
          <DataTable.DataRow.NumberCell
            value={input?.value}
            unit={input?.unit ?? ""}
          />
        );
      },
    },
    {
      key: "adjusted_volume",
      label: `Adjusted ${measurementTypeHeader}`,
      alignment: "right",
      renderComponent: ({ item }: { item: FlowType }) => {
        const output = (run as any)?.output.outputs.find(
          ({ asset_id, measurement_type }: any) =>
            asset_id === item.id && measurement_type === measurementType
        );

        return (
          <DataTable.DataRow.NumberCell
            value={output?.measurement_value}
            unit={output ? getUnitName(output.measurement_unit) : ""}
          />
        );
      },
    },
  ];

  const items = flows?.data ?? [];
  const isLoading =
    isNetworkLoading ||
    isFlowsLoading ||
    isRunLoading ||
    isConfigurationLoading ||
    runEstimationMethod.isLoading ||
    updateEstimationMethodConfiguration.isLoading ||
    applyConfiguration.isLoading ||
    isLoadingNetworkCalculationFlowHeaders ||
    isEstimationMethodLoading;
  const isFetching =
    isNetworkFetching ||
    isFlowsFetching ||
    isRunFetching ||
    isConfigurationFetching;

  const actionRow = [
    <Button
      key="apply_configurations"
      onClick={onClickApplyConfiguration}
      isLoading={applyConfiguration.isLoading}
    >
      Apply Configuration
    </Button>,
    <Button
      key="save"
      variant="primary"
      onClick={onClickSave}
      isLoading={runEstimationMethod.isLoading}
    >
      Calculate
    </Button>,
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{ title: "Consumers", actionRow }}
      dataTableProps={{
        headers,
        items: items as FlowType[],
        isLoading,
        isBusy: isFetching,
        getItemId: (item) => item.id,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: flows?.total_entries,
        },
      }}
    />
  );
};
